import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link, NavLink, Redirect, Route, Switch } from 'react-router-dom';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import StudentAnalytics from 'components/Admin/StudentAnalytics';
import ActivityScores from 'components/Course/ActivityScores';
import AllClassesAttendance from 'components/Course/AllClassesAttendance';
import EnrolledStudents from 'components/Course/EnrolledStudents';
import StudentAttendance from 'components/Course/StudentAttendance';
import TeamMembers from 'components/Course/TeamMembers';
import Dialog from 'components/Dialog';
import EmptyState from 'components/EmptyState';
import Tabs, { TabsProps } from 'components/Tabs';
import { Button, TextField } from 'components/UtilElements';
import { apiErrorHandler } from 'core/errors';
import { Dispatch } from 'redux';
import { COURSE_DELETED, SET_AUTH_STATUS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import { ReactComponent as AttendanceIcon } from 'assets/attendance_active.svg';
import { ReactComponent as CoursesIcon } from 'assets/courses_active.svg';
import { ReactComponent as InsightsIcon } from 'assets/insights_active.svg';
import { ReactComponent as LogoutIcon } from 'assets/logout_active.svg';
import { ReactComponent as MembersIcon } from 'assets/members_active.svg';
import { ReactComponent as ScoresIcon } from 'assets/scores_active.svg';
import { ReactComponent as TrashIcon } from 'assets/trash.svg';

interface DeleteCourseResponse extends MessageResponse {
  displayMessage: string;
}

function deleteCourseRequest() {
  return request<DeleteCourseResponse>({
    url: API.COURSE_DELETE,
    method: 'DELETE',
  });
}

type CourseSections = 'members' | 'scores' | 'analytics' | 'attendance' | 'settings';

interface RouteParams {
  section: CourseSections;
}

interface CourseDashboardProps extends RouteComponentProps<RouteParams>, ToastManagerProp {
  getAccess: SessionData | null;
  setAuthorization(isAuthorized: boolean): void;
  courseDeleted(): void;
}

interface CourseDashboardState {
  activeTab: CourseSections;
  activeSubTab: {
    members: 'enrolled-students' | 'team-members';
    attendance: 'attendance-class' | 'attendance-students';
    settings: 'settings-one' | 'settings-two';
  };
  hideTabHeader: boolean;
  isDeleteCourseDialogOpen: boolean;
  deleteCourseInput: string;
  isDeletingCourse: boolean;
}

class CourseDashboard extends Component<CourseDashboardProps, CourseDashboardState> {
  constructor(props: CourseDashboardProps) {
    super(props);
    this.state = {
      activeTab: this.props.match.params.section || 'members',
      activeSubTab: {
        members: 'enrolled-students',
        attendance: 'attendance-class',
        settings: 'settings-one',
      },
      hideTabHeader: false,
      isDeleteCourseDialogOpen: false,
      deleteCourseInput: '',
      isDeletingCourse: false,
    };
  }

  componentDidUpdate(prevProps: CourseDashboardProps) {
    if (this.props.location !== prevProps.location) {
      this.setState({ activeTab: this.props.match.params.section || 'members' });
    }
  }

  toggleTabHeader = () => {
    this.setState({ hideTabHeader: !this.state.hideTabHeader });
  };

  MembersTab = (): TabsProps => ({
    sections: [
      {
        header: 'Enrolled Students',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              members: 'enrolled-students',
            },
          }),
        key: 'enrolled-students',
        active: this.state.activeSubTab.members === 'enrolled-students',
        body: <EnrolledStudents {...this.props} />,
      },
      {
        header: 'Team Members',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              members: 'team-members',
            },
          }),
        key: 'team-members',
        active: this.state.activeSubTab.members === 'team-members',
        body: <TeamMembers {...this.props} />,
      },
    ],
  });

  AttendanceTab = (): TabsProps => ({
    sections: [
      {
        header: 'Classes',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              attendance: 'attendance-class',
            },
          }),
        key: 'attendance-class',
        active: this.state.activeSubTab.attendance === 'attendance-class',
        body: <AllClassesAttendance />,
      },
      {
        header: 'Individual Students',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              attendance: 'attendance-students',
            },
          }),
        key: 'attendance-students',
        active: this.state.activeSubTab.attendance === 'attendance-students',
        body: <StudentAttendance />,
      },
    ],
  });

  // Currently almost a copy of AttendanceTab
  SettingsTab = (): TabsProps => ({
    sections: [
      {
        header: 'Classes',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              settings: 'settings-one',
            },
          }),
        key: 'settings-one',
        active: this.state.activeSubTab.settings === 'settings-one',
        body: <EmptyState />,
      },
      {
        header: 'Individual Students',
        onclick: () =>
          this.setState({
            activeSubTab: {
              ...this.state.activeSubTab,
              settings: 'settings-two',
            },
          }),
        key: 'settings-two',
        active: this.state.activeSubTab.settings === 'settings-two',
        body: <EmptyState />,
      },
    ],
  });

  private deleteCourseDialog() {
    return (
      <Dialog
        isOpen={this.state.isDeleteCourseDialogOpen}
        classes={{ dialog: 'delete-course' }}
        variant='medium'
        title='Delete course'
        onClose={() => this.setState({ isDeleteCourseDialogOpen: false })}
        footer={
          <>
            <Button
              variant='text'
              color='success'
              size='small'
              onClick={() => this.setState({ isDeleteCourseDialogOpen: false })}
              preventHoverStyles
            >
              Cancel
            </Button>
            <Button
              variant='text'
              color='error'
              size='small'
              disabled={
                this.state.isDeletingCourse ||
                this.state.deleteCourseInput.toLowerCase() !== 'permanently delete'
              }
              onClick={async () => {
                this.setState({ isDeletingCourse: true });
                try {
                  const response = await deleteCourseRequest();
                  this.props.courseDeleted();

                  if (this.props.getAccess?.courseDetails.hasChildren === 1) {
                    this.props.history.replace(ROUTES.COURSES_SELECT_CHILD);
                    return;
                  }

                  this.props.history.replace(ROUTES.MESSAGE_PAGE, { message: response.displayMessage });
                  this.props.setAuthorization(false);
                } catch (error: any) {
                  apiErrorHandler(this.props.toastManager, error);
                  this.setState({ isDeletingCourse: false });
                }
              }}
              preventHoverStyles
            >
              {this.state.isDeletingCourse ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p>
          Deleting this course would remove all the data related to this course from Acadly. However, the
          grades exported from Acadly into your LMS will still remain in the LMS.
        </p>
        <p>
          This process is irreversible. To continue, please type &ldquo;permanently delete&rdquo; in the field
          below.
        </p>
        <TextField
          wrapperClassName='delete-course__input'
          placeholder='Please type “permanently delete”'
          value={this.state.deleteCourseInput}
          onchange={(e) => this.setState({ deleteCourseInput: e.target.value })}
        />
      </Dialog>
    );
  }

  render() {
    return (
      <div className='dashboard'>
        <nav className='dashboard__nav'>
          <ul className='nav'>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.COURSE_MEMBERS}>
                <MembersIcon className='nav__icon' /> Members
              </NavLink>
            </li>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.COURSE_INSIGHTS}>
                <InsightsIcon className='nav__icon' /> Insights
              </NavLink>
            </li>
            <li>
              <NavLink
                activeClassName='nav__item--active'
                className='nav__item'
                to={ROUTES.COURSE_ATTENDANCE}
              >
                <AttendanceIcon className='nav__icon' /> Attendance
              </NavLink>
            </li>
            <li>
              <NavLink activeClassName='nav__item--active' className='nav__item' to={ROUTES.COURSE_SCORES}>
                <CoursesIcon className='nav__icon' /> Scores
              </NavLink>
            </li>
            {false && (
              <li>
                <NavLink
                  activeClassName='nav__item--active'
                  className='nav__item'
                  to={ROUTES.COURSE_SETTINGS}
                >
                  <ScoresIcon className='nav__icon' /> Scores
                </NavLink>
              </li>
            )}
            <li>
              <Link className='nav__item' to={ROUTES.LOG_OUT}>
                <LogoutIcon className='nav__icon' /> Log Out
              </Link>
            </li>
          </ul>
        </nav>
        <div className='dashboard__other-actions'>
          <ul className='nav'>
            <li
              className='nav__item nav__item--danger'
              onClick={() => this.setState({ isDeleteCourseDialogOpen: true })}
            >
              <TrashIcon className='nav__icon' /> Delete Course
            </li>
          </ul>
        </div>
        <main className='dashboard__body'>
          <Switch>
            <Route path={ROUTES.COURSE_MEMBERS} render={() => <Tabs {...this.MembersTab()} />} />
            <Route path={ROUTES.COURSE_SCORES}>
              <ActivityScores />
            </Route>
            <Route path={ROUTES.COURSE_INSIGHTS}>
              <StudentAnalytics />
            </Route>
            <Route path={ROUTES.COURSE_ATTENDANCE} render={() => <Tabs {...this.AttendanceTab()} />} />
            <Route path={ROUTES.COURSE_SETTINGS} render={() => <Tabs {...this.SettingsTab()} />} />
            <Redirect to={ROUTES.COURSE_MEMBERS} />
          </Switch>
        </main>
        {this.deleteCourseDialog()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  getAccess: state.getAccess,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setAuthorization: (isAuthorized: boolean) => {
    dispatch({
      type: SET_AUTH_STATUS,
      payload: isAuthorized,
    });
  },
  courseDeleted: () => {
    dispatch({
      type: COURSE_DELETED,
    });
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withToastManager(CourseDashboard)));
